import {Alert, Button, Choice, DataStatus, Separator} from 'platform/components';
import {Box, Heading, Hide, HStack, Scroll, Show, VStack} from 'platform/foundation';

import {defaultTo} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {
  Basket,
  BasketMechanic,
  BasketSummary,
  EitherQuantityOrError,
  getOptionsFromMechanics,
  useBasketItems,
} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {AfterSalesMaterialReturnBasketItem} from './components/AfterSalesMaterialReturnBasketItem';
import {MaterialReturnBasketItem} from './types/MaterialReturnBasketItem';

const BASKET_TITLE_HEIGHT_IN_REM = 3.5;

export interface AfterSalesMaterialReturnBasketProps<T extends MaterialReturnBasketItem>
  extends RequiredTestIdProps {
  basket: Basket<T>;
  mechanic: BasketMechanic;
  onQuantityChange: (itemId: string, quantity: EitherQuantityOrError) => Promise<void>;
  onDelete: (itemsIds: string[]) => Promise<void>;
}

export function AfterSalesMaterialReturnBasket<T extends MaterialReturnBasketItem>(
  props: AfterSalesMaterialReturnBasketProps<T>
) {
  const {
    basketItems,
    basketItemsCount,
    hasSomeBasketItemsSelected,
    hasAllBasketItemsSelected,
    onSelectBasketItem,
    onSelectAllBasketItems,
    onBulkDeleteBasketItems,
  } = useBasketItems({basketItems: props.basket?.items, onDeleteBasketItems: props.onDelete});

  const basketTitle = `${i18n.t('general.labels.addedItems')} (${basketItemsCount})`;

  const basketTotalPrice = props.basket?.totalPrice;

  return (
    <Box
      borderRadius="small"
      boxShadow="elevation_1"
      backgroundColor="palettes.white.10.100"
      height="100%"
      data-testid={suffixTestId('wrapper', props)}
    >
      <VStack height="100%">
        <Box padding={4}>
          <Heading size={4} data-testid={suffixTestId('basketTitle', props)}>
            {basketTitle}
          </Heading>
        </Box>
        <Separator spacing={0} />
        <Box padding={4} height="100%" maxHeight={`calc(100% - ${BASKET_TITLE_HEIGHT_IN_REM}rem)`}>
          <VStack height="100%" spacing={3}>
            <Choice
              isRequired={props.mechanic?.isMechanicRequired}
              isDisabled={props.basket.hasPendingRequest}
              isLoading={props.mechanic?.areMechanicsLoading}
              label={i18n.t('entity.order.labels.mechanic')}
              value={defaultTo('', props.mechanic?.selectedMechanicId)}
              options={defaultTo([], getOptionsFromMechanics(props.mechanic?.mechanics))}
              onChange={props.mechanic?.onMechanicChange}
              errorMessage={props.mechanic?.mechanicError}
              data-testid={suffixTestId('assignedMechanic', props)}
            />

            <Show when={props.basket.hasInvalidItems}>
              <Alert
                type="inline"
                variant="error"
                title={i18n.t('general.notifications.basketHasInvalidItems')}
                data-testid={suffixTestId('alert.hasInvalidItems', props)}
              />
            </Show>

            <HStack justify="space-between">
              <Hide when={isNilOrEmpty(basketItems)}>
                <Button
                  isDisabled={props.basket.hasPendingRequest}
                  type="button"
                  variant="link"
                  leftIcon="action/done_all"
                  title={
                    hasAllBasketItemsSelected
                      ? i18n.t('general.actions.unselectAll')
                      : i18n.t('general.actions.selectAll')
                  }
                  onClick={onSelectAllBasketItems}
                  data-testid={suffixTestId('actions.toggleSelect', props)}
                />
              </Hide>

              <Show when={hasSomeBasketItemsSelected}>
                <Button
                  isDisabled={props.basket.hasPendingRequest}
                  type="button"
                  variant="dangerLink"
                  leftIcon="navigation/close"
                  title={i18n.t('general.actions.remove')}
                  onClick={onBulkDeleteBasketItems}
                  data-testid={suffixTestId('actions.remove', props)}
                />
              </Show>
            </HStack>

            <Scroll flex={1} auto>
              <VStack height="100%" spacing={4}>
                <DataStatus
                  isLoading={props.basket.isLoading}
                  isError={props.basket.hasError}
                  isEmpty={basketItemsCount === 0}
                  emptyMessage={i18n.t('general.notifications.noItemsSelected')}
                  minHeight="100%"
                >
                  {basketItems?.map((basketItem, index) => (
                    <AfterSalesMaterialReturnBasketItem
                      key={basketItem.id}
                      item={basketItem}
                      onSelect={onSelectBasketItem}
                      onQuantityChange={props.onQuantityChange}
                      onDelete={props.onDelete}
                      hasPendingRequest={props.basket.hasPendingRequest}
                      data-testid={suffixTestId(`basketItem-[${index}]`, props)}
                    />
                  ))}
                </DataStatus>
              </VStack>
            </Scroll>

            <Separator />

            <BasketSummary
              totalPrice={basketTotalPrice}
              data-testid={suffixTestId('summary', props)}
            />
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
